import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { useTranslation } from 'react-i18next';
import {
  AgentChat,
  ChatInput,
  ExpandableVideo,
  ExpandableBook,
} from '../TeachingAgent';
import { useTeachingAgent } from '../../hooks/useTeachingAgent';
import { useCompanionStore } from '../../store/companionStore';
import { useIDEStore } from '../../store/ideStore';
import {
  resolveCompanionHitlContext,
  useCanonicalLearningContextStore,
} from '../../store/canonicalLearningContextStore';
import { HitlPane } from '../HITL';
import { RailActionChips } from '../TutorChat';
import { isHitlFeatureEnabled } from '../../services/hitl';
import styles from './CompanionChat.module.css';

// Opt-in so the frontend can deploy safely before migration/runtime readiness.
// Unset or false removes the affordance and is the fail-closed rollback path.
const HITL_ENABLED = isHitlFeatureEnabled();

/**
 * CompanionChat — the live tutor conversation inside the Companion dock.
 *
 * Wires the existing teaching surface (useTeachingAgent +
 * AgentChat/ChatInput/AgentStatus + Expandable video/book) into the
 * platform-wide dock, so the Companion is functional on every screen — not
 * just the lab IDE (docs/ide-ux-spec.md §4).
 *
 * studentId comes from ZewstID (the OIDC `sub`), NOT the legacy authStore:
 * ApiBridge only mirrors the access token into authStore, never the user, so
 * `authStore.user` is null under OIDC. (The in-lab TeachingSidebar, still
 * gated on authStore.user, is effectively dead post-OIDC — to be fixed when it
 * is consolidated into this dock.)
 *
 * The active canonical item, or a standalone lab resolved by the server,
 * bounds assistance. A course overview has no general-purpose chat fallback.
 */
const CompanionChat: React.FC = () => {
  const { t } = useTranslation();
  const auth = useAuth();
  const studentId = auth.user?.profile?.sub;

  // Canonical item ownership takes precedence over the standalone IDE store.
  const labContext = useCompanionStore((s) => s.labContext);
  const lectureContext = useCompanionStore((s) => s.lectureContext);
  const canonicalContext = useCanonicalLearningContextStore(
    (state) => state.context,
  );
  const sessionLabContext = canonicalContext
    ? canonicalContext.item?.labId === labContext?.labId ? labContext ?? undefined : undefined
    : labContext ?? undefined;
  const hasLearningContext = Boolean(canonicalContext?.item?.id || sessionLabContext?.labId);

  const [videoExpanded, setVideoExpanded] = useState(false);
  const [bookExpanded, setBookExpanded] = useState(false);
  // When the opt-in surface is mounted, pause on the very first paint until
  // GET current proves there is no active or unacknowledged lifecycle.
  const [humanTakeoverActive, setHumanTakeoverActive] = useState(HITL_ENABLED);

  const readLegacyCodeSnapshot = useCallback(() => {
    const ide = useIDEStore.getState();
    if (!ide.activeFile) return undefined;
    const file = ide.files.get(ide.activeFile);
    if (!file) return undefined;
    return { file_path: file.path, content: file.content };
  }, []);
  const hitlContext = useMemo(
    () =>
      resolveCompanionHitlContext(
        lectureContext?.videoExternalId,
        readLegacyCodeSnapshot,
      ),
    [
      canonicalContext,
      lectureContext?.videoExternalId,
      readLegacyCodeSnapshot,
    ],
  );
  const persistenceContext = useMemo(() => {
    if (canonicalContext) {
      const item = canonicalContext.item;
      return item
        ? `course:${canonicalContext.courseSlug}:${item.type}:${item.id}`
        : `course:${canonicalContext.courseSlug}:overview`;
    }
    if (labContext?.labId) return `lab:${labContext.labId}`;
    if (lectureContext?.videoExternalId) {
      return `lecture:${lectureContext.videoExternalId}`;
    }
    return 'general';
  }, [
    canonicalContext,
    labContext?.labId,
    lectureContext?.videoExternalId,
  ]);

  // Hooks must run unconditionally; fall back to a placeholder id when the
  // subject is somehow absent (the dock only renders on authenticated screens,
  // so this is a guard, not an expected path). No session is created until the
  // student sends a message, so the fallback id never reaches the backend.
  const {
    messages,
    isStreaming,
    isThinking,
    currentTool,
    trace,
    sessionId,
    currentVideo,
    currentBook,
    railActions,
    dismissRailActions,
    error,
    sendMessage,
    ensureSession,
    interrupt,
    dismissVideo,
    dismissBook,
  } = useTeachingAgent({
    // The canonical shell wins over a leftover standalone lab store. The
    // backend derives the actual course/lab policy from this item UUID.
    courseItemId: canonicalContext?.item?.id,
    courseId: canonicalContext?.courseSlug,
    labContext: sessionLabContext,
    persistence: studentId && hasLearningContext
      ? { subject: studentId, context: persistenceContext }
      : undefined,
  });

  useEffect(() => {
    if (humanTakeoverActive && isStreaming) {
      void interrupt();
    }
  }, [humanTakeoverActive, interrupt, isStreaming]);

  const regenerate = useCallback(() => {
    const lastUser = [...messages].reverse().find((m) => m.role === 'user');
    if (lastUser && hasLearningContext && !humanTakeoverActive) {
      void sendMessage(lastUser.content);
    }
  }, [messages, hasLearningContext, humanTakeoverActive, sendMessage]);

  if (!studentId) {
    return <div className={styles.fallback}>{t('companion.signed_out')}</div>;
  }

  return (
    <div className={styles.chat}>
      {/* Activity and errors render in-thread (AgentChat): the bubble status
          line and the alert below the messages. A separate status bar here
          would repeat both. */}
      <div className={styles.messages}>
        {!hasLearningContext && <p className={styles.fallback}>{t('companion.open_context_hint', { name: t('companion.name') })}</p>}
        <AgentChat
          messages={messages}
          isStreaming={isStreaming}
          error={error}
          currentTool={currentTool}
          trace={trace}
          isThinking={isThinking}
          sessionId={sessionId}
          onStop={() => void interrupt()}
          onRegenerate={regenerate}
          onSuggestionClick={(suggestion) => {
            if (hasLearningContext && !humanTakeoverActive) void sendMessage(suggestion);
          }}
        />

        <RailActionChips actions={railActions} onAct={dismissRailActions} />

        {currentVideo && (
          <ExpandableVideo
            video={currentVideo}
            expanded={videoExpanded}
            onExpand={() => setVideoExpanded(true)}
            onCollapse={() => setVideoExpanded(false)}
            onFullscreen={() => setVideoExpanded(true)}
            onDismiss={dismissVideo}
          />
        )}

        {currentBook && (
          <ExpandableBook
            book={currentBook}
            expanded={bookExpanded}
            onExpand={() => setBookExpanded(true)}
            onCollapse={() => setBookExpanded(false)}
            onSplitView={() => setBookExpanded(true)}
            onDismiss={dismissBook}
          />
        )}
      </div>

      {HITL_ENABLED && hasLearningContext && (
        <HitlPane
          ensureTeachingSession={ensureSession}
          lectureId={hitlContext.sourceContentId}
          getCodeSnapshot={hitlContext.getCodeSnapshot}
          onAgentPausedChange={setHumanTakeoverActive}
        />
      )}

      <ChatInput
        onSend={sendMessage}
        onInterrupt={interrupt}
        isStreaming={isStreaming}
        disabled={humanTakeoverActive || !hasLearningContext}
        placeholder={
          humanTakeoverActive
            ? t('companion.paused_placeholder', { name: t('companion.name') })
            : !hasLearningContext ? t('companion.no_context_placeholder') : t('companion.composer_placeholder')
        }
      />
    </div>
  );
};

export default CompanionChat;
